import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Share2, Send, Copy, RefreshCw, Eye, EyeOff, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ShareInviteCard — one-tap surface to hand out a Government Shield
 * invite code to a trusted contact via native share / Telegram /
 * clipboard. Lives at the top of the Invites tab so opening the dialog
 * gives users an immediate, obvious path to grow their network.
 *
 * Privacy-first by design:
 *  - The share URL points at /register?invite=<code> on this same
 *    deployment (no 3rd-party short-link service, no click tracking).
 *  - The code itself is blurred until hover so a shoulder-surfer
 *    doesn't capture it while the user is picking a share target.
 *  - Native Web Share API is preferred (handles Signal/WhatsApp/iOS
 *    share sheet natively); Telegram and Copy are explicit fallbacks.
 */
export const ShareInviteCard = ({ invites, onCreateInvite, creating }) => {
  const [revealed, setRevealed] = useState(false);
  const [minting, setMinting] = useState(false);

  // Pick the freshest non-exhausted invite we have. An "exhausted"
  // invite is one where uses >= max_uses; re-sharing it would just
  // frustrate the recipient with an "invite already used" error.
  const activeInvite = useMemo(() => {
    if (!invites || invites.length === 0) return null;
    const live = invites.filter((i) => (i.uses || 0) < (i.max_uses || 1));
    if (live.length === 0) return null;
    return [...live].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    )[0];
  }, [invites]);

  // Auto-mint one on first mount if the user has no live invite on
  // hand — we want the share card to be immediately actionable, not a
  // "click Create, then scroll down, then copy" treasure hunt.
  useEffect(() => {
    let cancelled = false;
    if (!invites) return;
    if (invites.length > 0) return;
    if (creating || minting) return;
    setMinting(true);
    Promise.resolve(onCreateInvite?.())
      .finally(() => { if (!cancelled) setMinting(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invites, creating]);

  const shareUrl = useMemo(() => {
    if (!activeInvite) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/register?invite=${encodeURIComponent(activeInvite.code)}`;
  }, [activeInvite]);

  const shareText = useMemo(() => {
    if (!activeInvite) return '';
    return (
      `You've been invited to Government Shield — an end-to-end encrypted, ` +
      `invite-only messenger. Use code ${activeInvite.code} to create your account.`
    );
  }, [activeInvite]);

  const handleNativeShare = async () => {
    if (!activeInvite) return;
    const data = {
      title: 'Government Shield invite',
      text: shareText,
      url: shareUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch (_e) {
      // User aborted the share sheet — no toast, no noise.
      if (_e?.name === 'AbortError') return;
    }
    // Fallback: copy link so the user isn't dead-ended.
    handleCopyLink();
  };

  const handleTelegram = () => {
    if (!activeInvite) return;
    const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(tgUrl, '_blank', 'noopener,noreferrer');
  };

  const handleCopyLink = async () => {
    if (!activeInvite) return;
    try {
      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
      toast.success('Invite link copied — paste it into any chat');
    } catch (_e) {
      toast.error('Clipboard is blocked in this browser. Try the Share button instead.');
    }
  };

  const handleRotate = async () => {
    setMinting(true);
    try {
      await onCreateInvite?.();
      // Parent already toasts "Invite code created!" — don't double up.
    } finally {
      setMinting(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-4 space-y-3"
      data-testid="share-invite-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-white flex items-center gap-2">
            <Share2 className="w-4 h-4 text-primary" />
            Invite a trusted contact
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            One-tap share. Your invite is a single-use code that expires in 48h.
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setRevealed((v) => !v)}
          title={revealed ? 'Hide code' : 'Reveal code'}
          data-testid="share-invite-reveal-toggle"
        >
          {revealed ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </Button>
      </div>

      {/* Code display — blurred until user hovers or clicks reveal. */}
      <div
        className="group relative rounded-lg bg-secondary/50 border border-border px-3 py-2.5 overflow-hidden"
        data-testid="share-invite-code-box"
      >
        {activeInvite ? (
          <p
            className={`font-mono text-lg text-white tracking-[0.2em] select-all transition-all duration-150 ${
              revealed ? '' : 'blur-sm group-hover:blur-none'
            }`}
            data-testid="share-invite-code"
          >
            {activeInvite.code}
          </p>
        ) : (
          <p className="flex items-center gap-2 text-sm text-slate-500 font-mono">
            <Loader2 className="w-4 h-4 animate-spin" />
            {(creating || minting) ? 'Minting a fresh invite…' : 'No active invite — create one below'}
          </p>
        )}
        {activeInvite && (
          <p className="text-[10px] text-slate-500 mt-1">
            {(activeInvite.uses || 0)}/{activeInvite.max_uses || 1} used
            {activeInvite.expires_at && (
              <>
                {' · expires '}
                {new Date(activeInvite.expires_at).toLocaleString([], {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </>
            )}
          </p>
        )}
      </div>

      {/* Action row */}
      <div className="grid grid-cols-4 gap-2">
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleNativeShare}
          disabled={!activeInvite}
          className="col-span-1 gap-1"
          data-testid="share-invite-native-btn"
          title="Share via your device's share sheet (Signal, WhatsApp, etc.)"
        >
          <Share2 className="w-4 h-4" />
          <span className="hidden sm:inline">Share</span>
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleTelegram}
          disabled={!activeInvite}
          className="col-span-1 gap-1"
          data-testid="share-invite-telegram-btn"
          title="Share on Telegram"
        >
          <Send className="w-4 h-4" />
          <span className="hidden sm:inline">Telegram</span>
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleCopyLink}
          disabled={!activeInvite}
          className="col-span-1 gap-1"
          data-testid="share-invite-copy-btn"
          title="Copy the invite link + message"
        >
          <Copy className="w-4 h-4" />
          <span className="hidden sm:inline">Copy</span>
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRotate}
          disabled={minting || creating}
          className="col-span-1 gap-1 text-slate-400 hover:text-white"
          data-testid="share-invite-rotate-btn"
          title="Mint a fresh code (the current one stays valid until it expires)"
        >
          {(minting || creating) ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
          <span className="hidden sm:inline">New</span>
        </Button>
      </div>
    </div>
  );
};

export default ShareInviteCard;
